import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { IReport } from '../../types';
import {
  fetchPowerBiSharedReports as fetchPowerBiSharedReportsApiCall,
  fetchPowerBiReports as fetchPowerBiReportsApiCall,
  createSharedPowerBiReport as createSharedPowerBiReportApiCall,
  removeSharedPowerBiReport as removeSharedPowerBiReportApiCall,
  getRequest,
} from '../../utils/api';
import type { RootState } from '../store';

export const fetchPowerBiReports = createAsyncThunk(
  'powerBiReports/fetchStatus',
  async () => {
    const reportsResponse = await fetchPowerBiReportsApiCall();

    if (!reportsResponse?.error?.indexOf('‘Unauthorized’')) {
      const reports: IReport[] = reportsResponse.reports;

      const sharedReports: IReport[] = await fetchPowerBiSharedReportsApiCall();

      return { reports, sharedReports };
    }

    return { reports: [], sharedReports: [], isUnauthorized: true };
  }
);

export const fetchPowerBiReportsList = createAsyncThunk(
  'powerBiReports/fetchReportsListStatus',
  async () => {
    const reportsResponse = await fetchPowerBiReportsApiCall();

    const reports: IReport[] = reportsResponse.reports;

    return reports;
  }
);

export const createSharedPowerBiReport = createAsyncThunk(
  'powerBiReports/createStatus',
  async (id: string) => {
    const postShareResponse = await createSharedPowerBiReportApiCall(id);

    if (postShareResponse?.operationLocation) {
      await checkShareStatus(postShareResponse.operationLocation);
    }

    const sharedReports: IReport[] = await fetchPowerBiSharedReportsApiCall();

    return sharedReports;
  }
);

const POLLING_INTERVAL = 2500;
const POLLING_MAX_ATTEMPTS = 48;

const checkShareStatus = (url: string) => {
  return new Promise((resolve, reject) => {
    let counter: number = 0;
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    const timer = setInterval(async () => {
      const shareStatusResponse = await getRequest(url);
      if (shareStatusResponse?.status) {
        if (shareStatusResponse.status === 'Succeeded') {
          clearInterval(timer);
          resolve(shareStatusResponse.status);
          return;
        } else if (shareStatusResponse.status === 'Failed') {
          clearInterval(timer);
          reject(new Error('Operation failed'));
          return;
        }
      }
      counter += 1;
      if (counter === POLLING_MAX_ATTEMPTS) {
        clearInterval(timer);
        reject(new Error('Operation timed out'));
      }
    }, POLLING_INTERVAL);
  });
};

export const removeSharedPowerBiReport = createAsyncThunk(
  'powerBiReports/removeStatus',
  async (id: string) => {
    const removeShareResponse = await removeSharedPowerBiReportApiCall(id);

    if (removeShareResponse?.operationLocation) {
      await checkShareStatus(removeShareResponse.operationLocation);
    }

    const sharedReports: IReport[] = await fetchPowerBiSharedReportsApiCall();

    return sharedReports;
  }
);

export interface IPowerBiReportsState {
  reports: IReport[] | null;
  sharedReports: IReport[] | null;

  loading?: boolean;
  failed?: boolean;
  isUnauthorized?: boolean;
}

const initialState: IPowerBiReportsState = {
  reports: [],
  sharedReports: [],

  loading: false,
  failed: false,
  isUnauthorized: false,
};

export const powerBiReportsSlice = createSlice({
  name: 'powerBiReports',
  initialState,
  reducers: {
    clearReports: (state) => {
      state.reports = [];
      state.sharedReports = [];
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchPowerBiReports.fulfilled, (state, action) => {
        state.reports = action.payload.reports;
        state.sharedReports = action.payload.sharedReports;
        state.isUnauthorized = action.payload.isUnauthorized;
        state.loading = false;
        state.failed = false;
      })
      .addCase(fetchPowerBiReportsList.fulfilled, (state, action) => {
        state.reports = action.payload;
      })
      .addCase(createSharedPowerBiReport.fulfilled, (state, action) => {
        state.sharedReports = action.payload;
        state.loading = false;
        state.failed = false;
      })
      .addCase(removeSharedPowerBiReport.fulfilled, (state, action) => {
        state.sharedReports = action.payload;
        state.loading = false;
        state.failed = false;
      })
      .addCase(fetchPowerBiReports.pending, (state) => {
        state.reports = [];
        state.sharedReports = [];
        state.loading = true;
        state.failed = false;
      })
      .addCase(fetchPowerBiReports.rejected, (state) => {
        state.reports = [];
        state.sharedReports = [];
        state.loading = false;
        state.failed = true;
      });
  },
});

export const selectPowerBiReports = (state: RootState) => state.powerBiReports;

export default powerBiReportsSlice.reducer;
